/**
 * DeviceTimelineModal — Sprint 4.2A.
 *
 * Modal side-panel direita mostrando "Histórico Operacional" de um device.
 *
 * Fonte de dados (combinadas e ordenadas DESC por timestamp):
 *   1. GET /api/admin/devices/{id}/commands?limit=50  (existente Sprint 2)
 *      → eventos: relay_on/off, relay_reboot, reboot, ota_update, ping
 *      → cada com status: pending/delivered/acked/failed/expired
 *
 *   2. localStorage ipwd-device-state-changes (useDeviceStateChanges)
 *      → eventos: online/offline, rssi, relay_*, ota
 *      → detectados client-side ao longo da sessão (limitação documentada)
 *
 * Mostra TOP 20 (briefing). Scroll interno se passar disso.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { listCommands } from "../../lib/api.js";
import { readDeviceStateEvents } from "./useDeviceStateChanges.js";

const CMD_DISPLAY = {
  relay_on:     { icon: "▶",  label: "Relé ON",        color: "#22c55e" },
  relay_off:    { icon: "■",  label: "Relé OFF",       color: "#FF8A3C" },
  relay_reboot: { icon: "⟲",  label: "Reiniciar Relé", color: "#FFB224" },
  reboot:       { icon: "⟳",  label: "Reiniciar ESP32",color: "#FF5063" },
  ota_update:   { icon: "⬇",  label: "OTA",            color: "#60a5fa" },
  ping:         { icon: "◌",  label: "Ping",           color: "#94a3b8" },
};

const STATE_DISPLAY = {
  online:    { icon: "●", label: "Online",       color: "#22c55e" },
  offline:   { icon: "●", label: "Offline",      color: "#FF5063" },
  rssi:      { icon: "📶", label: "RSSI",         color: "#FFB224" },
  relay_on:  { icon: "▶", label: "Relé reportado ON",  color: "#22c55e" },
  relay_off: { icon: "■", label: "Relé reportado OFF", color: "#FF8A3C" },
  ota:       { icon: "⬇", label: "OTA state",    color: "#60a5fa" },
};

const CMD_STATUS_COLOR = {
  pending:   "#FFB224",
  delivered: "#60a5fa",
  acked:     "#22c55e",
  failed:    "#FF5063",
  expired:   "#94a3b8",
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Mostra HH:mm:ss se foi hoje, senão DD/MM HH:mm
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hms = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (sameDay) return hms;
  const dmy = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${dmy} ${hms}`;
}

export default function DeviceTimelineModal({ open, deviceId, onClose }) {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!open || !deviceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCommands(deviceId, { limit: 50 })
      .then((data) => {
        if (cancelled) return;
        // Backend retorna array direto OU { commands: [...] }
        const arr = Array.isArray(data) ? data : (data?.commands || []);
        setCommands(arr);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Erro ao carregar histórico");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, deviceId, refreshTick]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  // Lê transições do localStorage. Re-lê quando deviceId/open/refreshTick mudam.
  const stateEvents = useMemo(() => {
    if (!open || !deviceId) return [];
    return readDeviceStateEvents(deviceId);
  }, [open, deviceId, refreshTick]);

  const events = useMemo(() => {
    const merged = [];
    for (const c of commands || []) {
      const disp = CMD_DISPLAY[c.type] || { icon: "·", label: c.type, color: "#94a3b8" };
      merged.push({
        ts: c.created_at,
        icon: disp.icon,
        label: disp.label,
        color: disp.color,
        detail: c.status || "",
        detailColor: CMD_STATUS_COLOR[c.status] || "#94a3b8",
        kind: "command",
      });
    }
    for (const e of stateEvents) {
      const disp = STATE_DISPLAY[e.type] || { icon: "·", label: e.type, color: "#94a3b8" };
      merged.push({
        ts: e.ts,
        icon: disp.icon,
        label: disp.label,
        color: disp.color,
        detail: e.detail || "",
        kind: "state",
      });
    }
    merged.sort((a, b) => {
      const ta = new Date(a.ts).getTime() || 0;
      const tb = new Date(b.ts).getTime() || 0;
      return tb - ta;
    });
    return merged.slice(0, 20);
  }, [commands, stateEvents]);

  if (!open) return null;

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal — imune a transform/filter futuro
  // em .ipwd-fleet-focus (mapa fullscreen). z-[2000] segue acima do mapa e abaixo
  // da confirmação crítica (z-3000/4000).
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ipwd-timeline-modal">
        <div className="ipwd-timeline-header">
          <div>
            <div className="ipwd-timeline-title">📋 Histórico Operacional</div>
            <div className="ipwd-timeline-device">{deviceId}</div>
          </div>
          <div className="ipwd-timeline-header-actions">
            <button
              type="button"
              onClick={() => setRefreshTick((t) => t + 1)}
              disabled={loading}
              title="Recarregar"
              className="ipwd-timeline-refresh-btn"
            >
              {loading ? "…" : "↻"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ipwd-timeline-close-btn"
            >×</button>
          </div>
        </div>

        {error && (
          <div className="ipwd-timeline-error">{error}</div>
        )}

        <div className="ipwd-timeline-meta">
          <span>{events.length} de {(commands?.length || 0) + stateEvents.length} eventos</span>
          {stateEvents.length > 0 && (
            <span title="Eventos de estado detectados client-side ao longo da sessão">
              · {stateEvents.length} transições locais
            </span>
          )}
        </div>

        <div className="ipwd-timeline-list">
          {loading && events.length === 0 && (
            <div className="ipwd-timeline-empty">Carregando histórico…</div>
          )}
          {!loading && events.length === 0 && (
            <div className="ipwd-timeline-empty">
              Nenhum evento registrado para este device ainda.
              <br />
              <span className="ipwd-timeline-empty-hint">
                Comandos enfileirados via popup ou bulk aparecerão aqui.
              </span>
            </div>
          )}
          {events.map((ev, i) => (
            <div key={i} className={`ipwd-timeline-event is-${ev.kind}`}>
              <div className="ipwd-timeline-time">{fmtTime(ev.ts)}</div>
              <div className="ipwd-timeline-icon" style={{ color: ev.color }}>
                {ev.icon}
              </div>
              <div className="ipwd-timeline-body">
                <div className="ipwd-timeline-label" style={{ color: ev.color }}>
                  {ev.label}
                </div>
                {ev.detail && (
                  <div
                    className="ipwd-timeline-detail"
                    style={ev.detailColor ? { color: ev.detailColor } : undefined}
                  >
                    {ev.detail}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="ipwd-timeline-footer">
          <span>Top 20 eventos · ordenado por timestamp desc</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
